import { Language } from "./translations";

interface LandingCopy {
  meta: { title: string; description: string };
  eyebrow: string;
  edition: string;
  title: string;
  titleAccent: string;
  intro: string;
  searchCta: string;
  howCta: string;
  accessNote: string;
  contentsLabel: string;
  contents: { anchor: string; label: string }[];
  preview: {
    label: string;
    switchLabel: string;
    channel: string;
    open: string;
    examples: {
      tab: string;
      contextLabel: string;
      context: string;
      title: string;
      body: string;
      detail: string;
      note: string;
    }[];
  };
  search: {
    eyebrow: string;
    title: string;
    intro: string;
    link: string;
    categoryLabel: string;
    categoryTitle: string;
    categoryText: string;
    hierarchyLabel: string;
    hierarchy: string[];
    filters: { title: string; text: string }[];
    sample: {
      label: string;
      query: string;
      region: string;
      category: string;
      columns: string[];
      scrollHint: string;
      rows: { number: string; title: string; customer: string; amount: string; status: string }[];
      note: string;
    };
  };
  notifications: {
    eyebrow: string;
    title: string;
    intro: string;
    services: {
      tag: string;
      title: string;
      text: string;
      steps: string[];
      link: string;
    }[];
    note: string;
  };
  analysis: {
    eyebrow: string;
    title: string;
    intro: string;
    services: { path: string; title: string; text: string }[];
    previewLabel: string;
    previews: { inputTitle: string; inputs: string[]; outputTitle: string; outputs: string[]; link: string }[];
    note: string;
  };
  start: {
    eyebrow: string;
    title: string;
    intro: string;
    plans: string;
    register: string;
    steps: { title: string; text: string }[];
  };
  faq: {
    eyebrow: string;
    title: string;
    contact: string;
    items: { question: string; answer: string }[];
  };
}

export const landingCopy: Record<Language, LandingCopy> = {
  ru: {
    meta: {
      title: "G-Launcher — поиск и отслеживание госзакупок Казахстана",
      description:
        "Поиск лотов по категориям ЕНС ТРУ, подписка на новые закупки и уведомления об изменении статусов в Telegram. Поставщики, финансовый анализ допуска и планирование.",
    },
    eyebrow: "Для участников госзакупок Казахстана",
    edition: "Поиск. Наблюдение. Расчёт.",
    title: "Нужные закупки.",
    titleAccent: "В поле зрения.",
    intro:
      "Найдите лоты по своему направлению. Сохраните поиск, чтобы узнавать о новых закупках, и отслеживайте изменения в тех, которые уже выбрали.",
    searchCta: "Открыть поиск лотов",
    howCta: "Как работают уведомления",
    accessNote: "Первые 10 результатов — без регистрации. После регистрации можно включить 7 дней полного поиска без карты.",
    contentsLabel: "Возможности G-Launcher",
    contents: [
      { anchor: "search", label: "Найти подходящее" },
      { anchor: "notifications", label: "Быть в курсе изменений" },
      { anchor: "analysis", label: "Оценить возможности" },
    ],
    preview: {
      label: "Пример уведомления",
      switchLabel: "Выбрать пример уведомления",
      channel: "Уведомление",
      open: "Ссылка на закупку в G-Launcher",
      examples: [
        {
          tab: "Новый лот",
          contextLabel: "Вы сохранили поиск",
          context: "Технический надзор · Алматы",
          title: "Новый лот по вашему поиску",
          body: "Технический надзор за ремонтом здания",
          detail: "Регион: Алматы",
          note: "Новый лот появился в базе и подошёл под сохранённые фильтры. Вы получаете уведомление со ссылкой.",
        },
        {
          tab: "Изменение статуса",
          contextLabel: "Вы отслеживаете закупку",
          context: "Ремонт здания",
          title: "Статус закупки изменился",
          body: "Приём заявок → Рассмотрение заявок",
          detail: "Закупка из вашего списка отслеживания",
          note: "Сервис обнаружил изменение статуса выбранной закупки. Вы получаете уведомление со ссылкой.",
        },
      ],
    },
    search: {
      eyebrow: "Поиск лотов",
      title: "От направления бизнеса — к подходящему лоту.",
      intro:
        "Соберите нужную выборку из данных Госзакупа: от целой категории товаров и услуг до конкретного заказчика.",
      link: "Перейти к фильтрам",
      categoryLabel: "Категории ЕНС ТРУ",
      categoryTitle: "Несколько категорий. Один поиск.",
      categoryText:
        "Выберите целый раздел ЕНС ТРУ или несколько нужных категорий. Это удобно, если вы работаете сразу по нескольким направлениям.",
      hierarchyLabel: "Структура выбора ЕНС ТРУ",
      hierarchy: ["Категория", "Подкатегория", "Лоты"],
      filters: [
        {
          title: "Предмет закупки",
          text: "Наименование, описание и коды ЕНС ТРУ.",
        },
        {
          title: "Место и заказчик",
          text: "Регион поставки, наименование или БИН заказчика.",
        },
        { title: "Условия", text: "Сумма, статус, способ закупки и даты." },
        {
          title: "Дальнейшие действия",
          text: "Откройте лот, сохраните поиск или добавьте закупку в отслеживание.",
        },
      ],
      sample: {
        label: "Пример с тестовыми данными",
        query: "Технический надзор",
        region: "Алматы",
        category: "71 · Архитектура и инженерные услуги",
        columns: ["№ объявления", "Наименование лота", "Сумма, ₸", "Статус"],
        scrollHint: "Листайте таблицу по горизонтали",
        rows: [
          { number: "17700001-1", title: "Технический надзор за ремонтом здания", customer: "Учебный центр · Алматы", amount: "8 450 000", status: "Приём заявок" },
          { number: "17700002-1", title: "Технический надзор за благоустройством территории", customer: "Городское учреждение · Алматы", amount: "12 600 000", status: "Приём заявок" },
          { number: "17700003-1", title: "Технический надзор за реконструкцией сетей", customer: "Коммунальное предприятие · Алматы", amount: "6 280 000", status: "Опубликован" },
        ],
        note: "В поиске — реальные лоты. Здесь показан пример работы с выбранными фильтрами.",
      },
    },
    notifications: {
      eyebrow: "Уведомления",
      title: "Не возвращайтесь к одному и тому же поиску каждый день.",
      intro:
        "Новые возможности и изменения в выбранной закупке — два отдельных сценария. Подключите тот, который нужен вам.",
      services: [
        {
          tag: "Найти то, чего ещё нет в выборке",
          title: "Подписка на поиск",
          text: "Вы задаёте фильтры. Когда в базе появляются новые подходящие лоты, сервис собирает совпадения и уведомляет вас.",
          steps: [
            "Настройте фильтры на странице «Поиск лотов».",
            "Нажмите «Подписаться на поиск», сохраните условия и выберите канал уведомлений.",
            "Смотрите совпадения на сайте. Для сообщений в Telegram включите этот канал, если он доступен в вашем тарифе.",
          ],
          link: "К подпискам на поиск",
        },
        {
          tag: "Следить за тем, что уже выбрали",
          title: "Отслеживание лотов",
          text: "Добавьте интересующую закупку. Сервис проверяет её статус и появление протокола обсуждения, затем сообщает об изменениях в Telegram.",
          steps: [
            "Добавьте закупку из таблицы, карточки лота или по номеру.",
            "Привяжите Telegram и подтвердите платное добавление.",
            "Получайте события по всей закупке: один слот отслеживания на одно объявление.",
          ],
          link: "К отслеживаемым закупкам",
        },
      ],
      note: "Telegram подключается к вашему аккаунту. Уведомления приходят после обновления данных и проверки сервисом; постоянное присутствие на сайте не требуется.",
    },
    analysis: {
      eyebrow: "Инструменты для решения",
      title: "Посмотрите на закупку со всех сторон.",
      intro:
        "Поиск — начало работы. Дальше пригодятся данные о поставщиках и расчёт финансовых показателей компании.",
      previewLabel: "Как устроен расчёт",
      previews: [
        { inputTitle: "Ваши данные", inputs: ["Категория и сумма закупки", "Доход, налоги и ФОТ за 3 года"], outputTitle: "Результат", outputs: ["Итоговый балл ПФУ", "Баллы по каждой составляющей"], link: "Посмотреть пример расчёта" },
        { inputTitle: "Ваши данные", inputs: ["Целевая сумма и год закупки", "Финансовые показатели прошлых лет"], outputTitle: "Результат", outputs: ["Необходимые доходы, налоги и ФОТ", "Показатели целевого года в тенге"], link: "Посмотреть пример планирования" },
      ],
      services: [
        {
          path: "/suppliers",
          title: "Поставщики",
          text: "Найдите компанию по БИН, названию или категориям ЕНС ТРУ. Изучите доступные сведения и договоры.",
        },
        {
          path: "/calculator",
          title: "Финансовый анализ допуска",
          text: "Выберите категорию и сумму закупки, укажите доход, налоги и ФОТ компании. Получите балл ПФУ и его составляющие.",
        },
        {
          path: "/financial-planning",
          title: "Финансовое планирование",
          text: "Узнайте, какие доходы, налоги и ФОТ нужны для целевой суммы закупки с учётом показателей прошлых лет.",
        },
      ],
      note: "Балл ПФУ ниже 140 означает, что компания имеет не максимальный балл. Сам по себе этот результат не определяет допуск к конкурсу.",
    },
    start: {
      eyebrow: "Начало работы",
      title: "Начните со своего первого поиска.",
      intro:
        "Попробуйте фильтры на реальных лотах. Затем включите пробный доступ на 7 дней: полный поиск лотов и поставщиков, один сохранённый поиск с уведомлениями в Telegram. Без карты и автоматических списаний.",
      plans: "Посмотреть тарифы",
      register: "Создать аккаунт",
      steps: [
        {
          title: "Найдите подходящие лоты",
          text: "Поиск без регистрации покажет первые 10 результатов. Попробуйте наименование, регион и категории ЕНС ТРУ.",
        },
        {
          title: "Включите 7 дней полного поиска",
          text: "После регистрации и подтверждения почты включите пробный доступ по кнопке. Сохраните один поиск и подключите Telegram.",
        },
        {
          title: "Выберите дальнейший доступ",
          text: "Продолжайте работу с подходящим тарифом. Расчёты и отслеживание закупок имеют отдельные лимиты, которые видны в кабинете.",
        },
      ],
    },
    faq: {
      eyebrow: "Перед началом",
      title: "Несколько важных деталей.",
      contact: "Задать вопрос",
      items: [
        {
          question: "Что можно попробовать бесплатно?",
          answer: "Без регистрации доступны первые 10 результатов поиска и демонстрации расчётов. После подтверждения аккаунта можно один раз включить 7 дней полного поиска лотов и поставщиков и один сохранённый поиск с уведомлениями на сайте и в Telegram. Карта не требуется. Пробный период не включает безлимитные расчёты и отслеживание статусов: для них используются отдельные остатки услуг.",
        },
        {
          question: "Чем подписка на поиск отличается от отслеживания?",
          answer:
            "Подписка на поиск находит новые лоты по сохранённым фильтрам. Отслеживание работает с конкретной закупкой, которую вы уже выбрали: сообщает о смене статуса и появлении протокола обсуждения. Эти услуги имеют отдельные условия доступа.",
        },
        {
          question: "Нужно ли оставлять сайт открытым?",
          answer:
            "Нет. Проверки выполняет сервис. Для сообщений в Telegram привяжите его к аккаунту и включите нужный канал уведомлений. Для подписки на поиск доступность Telegram зависит от тарифа.",
        },
        {
          question: "Когда расходуется слот отслеживания?",
          answer:
            "Перед платным добавлением вы увидите окно подтверждения. Один слот используется для одной закупки целиком, даже если в ней несколько лотов. Повторное добавление уже отслеживаемой закупки не списывает второй слот.",
        },
        {
          question: "Откуда берутся данные и где подать заявку?",
          answer:
            "Сервис использует данные портала государственных закупок Казахстана. Обновления появляются после получения и обработки этих данных. Из карточки можно перейти к источнику; подача заявки происходит на портале Госзакупа.",
        },
      ],
    },
  },
  kk: {
    meta: {
      title:
        "G-Launcher — Қазақстанның мемлекеттік сатып алуларын іздеу және бақылау",
      description:
        "ЕНС ТРУ санаттары бойынша лоттарды іздеу, жаңа сатып алуларға жазылу және мәртебе өзгерістері туралы Telegram хабарламалары. Жеткізушілер, қаржылық талдау және жоспарлау.",
    },
    eyebrow: "Қазақстанның мемлекеттік сатып алу қатысушыларына",
    edition: "Іздеу. Бақылау. Есептеу.",
    title: "Қажетті сатып алулар.",
    titleAccent: "Назарда болсын.",
    intro:
      "Өз бағытыңызға сай лоттарды табыңыз. Жаңа сатып алулар туралы білу үшін іздеуді сақтаңыз және таңдалған сатып алулардағы өзгерістерді бақылаңыз.",
    searchCta: "Лоттарды іздеуге өту",
    howCta: "Хабарламалар қалай жұмыс істейді",
    accessNote: "Алғашқы 10 нәтиже — тіркелусіз. Тіркелгеннен кейін толық іздеуді 7 күнге картасыз қосуға болады.",
    contentsLabel: "G-Launcher мүмкіндіктері",
    contents: [
      { anchor: "search", label: "Сәйкес лоттарды табу" },
      { anchor: "notifications", label: "Өзгерістерден хабардар болу" },
      { anchor: "analysis", label: "Мүмкіндіктерді бағалау" },
    ],
    preview: {
      label: "Хабарлама үлгісі",
      switchLabel: "Хабарлама үлгісін таңдау",
      channel: "Хабарлама",
      open: "G-Launcher-дегі сатып алуға сілтеме",
      examples: [
        {
          tab: "Жаңа лот",
          contextLabel: "Сіз іздеуді сақтадыңыз",
          context: "Техникалық қадағалау · Алматы",
          title: "Іздеуіңізге сәйкес жаңа лот",
          body: "Ғимаратты жөндеуге техникалық қадағалау",
          detail: "Өңір: Алматы",
          note: "Базаға жаңа лот қосылып, сақталған сүзгілерге сәйкес келді. Сіз сілтемесі бар хабарлама аласыз.",
        },
        {
          tab: "Мәртебенің өзгеруі",
          contextLabel: "Сіз сатып алуды бақылап отырсыз",
          context: "Ғимаратты жөндеу",
          title: "Сатып алу мәртебесі өзгерді",
          body: "Өтінімдерді қабылдау → Өтінімдерді қарау",
          detail: "Бақылау тізіміңіздегі сатып алу",
          note: "Сервис таңдалған сатып алудың мәртебесі өзгергенін анықтады. Сіз сілтемесі бар хабарлама аласыз.",
        },
      ],
    },
    search: {
      eyebrow: "Лоттарды іздеу",
      title: "Бизнес бағытыңыздан — сәйкес лотқа дейін.",
      intro:
        "Мемлекеттік сатып алу деректерінен қажетті іріктемені жасаңыз: тауарлар мен қызметтер санатынан бастап нақты тапсырыс берушіге дейін.",
      link: "Сүзгілерге өту",
      categoryLabel: "ЕНС ТРУ санаттары",
      categoryTitle: "Бірнеше санат. Бір іздеу.",
      categoryText:
        "ЕНС ТРУ бөлімін толық немесе бірнеше қажетті санатты таңдаңыз. Бірнеше бағытта жұмыс істейтіндер үшін ыңғайлы.",
      hierarchyLabel: "ЕНС ТРУ таңдау құрылымы",
      hierarchy: ["Санат", "Ішкі санат", "Лоттар"],
      filters: [
        {
          title: "Сатып алу нысаны",
          text: "Атауы, сипаттамасы және ЕНС ТРУ кодтары.",
        },
        {
          title: "Орын және тапсырыс беруші",
          text: "Жеткізу өңірі, тапсырыс берушінің атауы немесе БСН.",
        },
        {
          title: "Шарттар",
          text: "Сома, мәртебе, сатып алу тәсілі және күндер.",
        },
        {
          title: "Келесі қадамдар",
          text: "Лотты ашыңыз, іздеуді сақтаңыз немесе сатып алуды бақылауға қосыңыз.",
        },
      ],
      sample: {
        label: "Тест деректері бар мысал",
        query: "Техникалық қадағалау",
        region: "Алматы",
        category: "71 · Сәулет және инженерлік қызметтер",
        columns: ["Хабарландыру №", "Лот атауы", "Сома, ₸", "Мәртебе"],
        scrollHint: "Кестені көлденең айналдырыңыз",
        rows: [
          { number: "17700001-1", title: "Ғимаратты жөндеуге техникалық қадағалау", customer: "Оқу орталығы · Алматы", amount: "8 450 000", status: "Өтінімдерді қабылдау" },
          { number: "17700002-1", title: "Аумақты абаттандыруға техникалық қадағалау", customer: "Қалалық мекеме · Алматы", amount: "12 600 000", status: "Өтінімдерді қабылдау" },
          { number: "17700003-1", title: "Желілерді қайта құруға техникалық қадағалау", customer: "Коммуналдық кәсіпорын · Алматы", amount: "6 280 000", status: "Жарияланды" },
        ],
        note: "Іздеуде нақты лоттар көрсетіледі. Мұнда таңдалған сүзгілермен жұмыс істеу мысалы берілген.",
      },
    },
    notifications: {
      eyebrow: "Хабарламалар",
      title: "Бір іздеуді күн сайын қайталаудың қажеті жоқ.",
      intro:
        "Жаңа мүмкіндіктер мен таңдалған сатып алудағы өзгерістер — екі бөлек міндет. Өзіңізге керегін қосыңыз.",
      services: [
        {
          tag: "Іріктемеде әлі жоқ лоттарды табу",
          title: "Іздеуге жазылу",
          text: "Сіз сүзгілерді орнатасыз. Базаға сәйкес жаңа лоттар қосылғанда, сервис сәйкестіктерді жинап, сізге хабарлайды.",
          steps: [
            "«Лоттарды іздеу» бетінде сүзгілерді орнатыңыз.",
            "«Іздеуге жазылу» батырмасын басып, шарттарды сақтаңыз және хабарлама арнасын таңдаңыз.",
            "Сәйкестіктерді сайттан көріңіз. Telegram хабарламалары үшін тарифте қолжетімді болса, осы арнаны қосыңыз.",
          ],
          link: "Іздеу жазылымдарына өту",
        },
        {
          tag: "Таңдалған сатып алуды бақылау",
          title: "Лоттарды бақылау",
          text: "Қажетті сатып алуды қосыңыз. Сервис оның мәртебесін және талқылау хаттамасының жариялануын тексеріп, өзгерістер туралы Telegram-ға хабарлайды.",
          steps: [
            "Сатып алуды кестеден, лот карточкасынан немесе нөмірі бойынша қосыңыз.",
            "Telegram-ды байланыстырып, ақылы қосуды растаңыз.",
            "Бүкіл сатып алу бойынша оқиғаларды алыңыз: бір хабарландыруға бір бақылау слоты.",
          ],
          link: "Бақыланатын сатып алуларға өту",
        },
      ],
      note: "Telegram аккаунтыңызға байланыстырылады. Хабарламалар деректер жаңартылып, сервис тексергеннен кейін келеді; сайтты ашық ұстаудың қажеті жоқ.",
    },
    analysis: {
      eyebrow: "Шешім қабылдау құралдары",
      title: "Сатып алуды жан-жақты бағалаңыз.",
      intro:
        "Іздеу — жұмыстың бастамасы. Әрі қарай жеткізушілер туралы деректер мен компанияның қаржылық көрсеткіштерін есептеу көмектеседі.",
      previewLabel: "Есептеу қалай жасалады",
      previews: [
        { inputTitle: "Сіздің деректеріңіз", inputs: ["Сатып алу санаты мен сомасы", "3 жылдық кіріс, салықтар және ЕТҚ"], outputTitle: "Нәтиже", outputs: ["ҚТК қорытынды балы", "Әр құрамдас бойынша балдар"], link: "Есептеу мысалын көру" },
        { inputTitle: "Сіздің деректеріңіз", inputs: ["Мақсатты сатып алу сомасы мен жылы", "Өткен жылдардың қаржылық көрсеткіштері"], outputTitle: "Нәтиже", outputs: ["Қажетті кіріс, салықтар және ЕТҚ", "Мақсатты жылдың теңгемен көрсеткіштері"], link: "Жоспарлау мысалын көру" },
      ],
      services: [
        {
          path: "/suppliers",
          title: "Жеткізушілер",
          text: "Компанияны БСН, атауы немесе ЕНС ТРУ санаттары бойынша табыңыз. Қолжетімді мәліметтер мен шарттарды қараңыз.",
        },
        {
          path: "/calculator",
          title: "Қатысуға рұқсатты қаржылық талдау",
          text: "Сатып алу санаты мен сомасын таңдаңыз, компанияның кірісін, салықтарын және ЕТҚ көрсетіңіз. ҚТК балы мен оның құрамдастарын алыңыз.",
        },
        {
          path: "/financial-planning",
          title: "Қаржылық жоспарлау",
          text: "Өткен жылдардың көрсеткіштерін ескеріп, мақсатты сатып алу сомасы үшін қандай кіріс, салықтар және ЕТҚ қажет екенін біліңіз.",
        },
      ],
      note: "ҚТК балы 140-тан төмен болса, компанияның балы ең жоғары емес. Бұл нәтиже өздігінен конкурсқа жіберілуін анықтамайды.",
    },
    start: {
      eyebrow: "Жұмысты бастау",
      title: "Алғашқы іздеуіңізден бастаңыз.",
      intro:
        "Сүзгілерді нақты лоттарда қолданып көріңіз. Содан кейін 7 күндік сынақ қолжетімділігін қосыңыз: лоттар мен жеткізушілерді толық іздеу және Telegram хабарламалары бар бір сақталған іздеу. Картасыз және автоматты төлемсіз.",
      plans: "Тарифтерді қарау",
      register: "Аккаунт жасау",
      steps: [
        {
          title: "Сәйкес лоттарды табыңыз",
          text: "Тіркелусіз іздеу алғашқы 10 нәтижені көрсетеді. Атау, өңір және ЕНС ТРУ санаттарын қолданып көріңіз.",
        },
        {
          title: "Толық іздеуді 7 күнге қосыңыз",
          text: "Тіркеліп, поштаны растағаннан кейін сынақ қолжетімділігін батырма арқылы қосыңыз. Бір іздеуді сақтап, Telegram-ды байланыстырыңыз.",
        },
        {
          title: "Кейінгі қолжетімділікті таңдаңыз",
          text: "Өзіңізге сәйкес тарифпен жұмысты жалғастырыңыз. Есептеулер мен сатып алуды бақылаудың жеке лимиттері кабинетте көрсетіледі.",
        },
      ],
    },
    faq: {
      eyebrow: "Бастамас бұрын",
      title: "Бірнеше маңызды мәлімет.",
      contact: "Сұрақ қою",
      items: [
        {
          question: "Нені тегін қолданып көруге болады?",
          answer: "Тіркелусіз алғашқы 10 іздеу нәтижесі мен есептеу мысалдары қолжетімді. Аккаунтты растағаннан кейін лоттар мен жеткізушілерді толық іздеуді және сайт пен Telegram хабарламалары бар бір сақталған іздеуді 7 күнге бір рет қосуға болады. Карта қажет емес. Сынақ мерзімі шексіз есептеулер мен мәртебелерді бақылауды қамтымайды: олар үшін қызметтердің жеке қалдықтары пайдаланылады.",
        },
        {
          question: "Іздеуге жазылу мен бақылаудың айырмашылығы қандай?",
          answer:
            "Іздеуге жазылу сақталған сүзгілер бойынша жаңа лоттарды табады. Бақылау таңдалған нақты сатып алумен жұмыс істейді: мәртебе өзгерісі мен талқылау хаттамасының жариялануы туралы хабарлайды. Бұл қызметтердің қолжетімділік шарттары бөлек.",
        },
        {
          question: "Сайтты ашық ұстау керек пе?",
          answer:
            "Жоқ. Тексерулерді сервис орындайды. Telegram хабарламалары үшін оны аккаунтқа байланыстырып, қажетті арнаны қосыңыз. Іздеуге жазылуда Telegram қолжетімділігі тарифке байланысты.",
        },
        {
          question: "Бақылау слоты қашан жұмсалады?",
          answer:
            "Ақылы қосудың алдында растау терезесі көрсетіледі. Бір слот бір сатып алуға толық қолданылады, оның ішінде бірнеше лот болса да. Бақылаудағы сатып алуды қайта қосу екінші слотты жұмсамайды.",
        },
        {
          question: "Деректер қайдан алынады және өтінім қайда беріледі?",
          answer:
            "Сервис Қазақстанның мемлекеттік сатып алу порталының деректерін пайдаланады. Жаңартулар деректер алынып, өңделгеннен кейін пайда болады. Карточкадан бастапқы дереккөзге өтуге болады; өтінім мемлекеттік сатып алу порталында беріледі.",
        },
      ],
    },
  },
};
